import React from "react";
import { Mic, GraduationCap, Building2, Users } from "lucide-react";
import CTABand from "../components/CTABand";
import { MEDIA } from "../lib/constants";
import { useSEO } from "../lib/seo";

const TOPICS = [
  {
    title: "Medicaid Pending Workflow That Actually Recovers Revenue",
    audience: "Operators, Business Office Leadership, and Corporate Finance",
  },
  {
    title: "Survey Readiness Beyond the Plan of Correction",
    audience: "Administrators, DONs, and Regional Directors",
  },
  {
    title: "Social Services as a Compliance and Liability Function",
    audience: "Social Services Directors and IDT Leadership",
  },
  {
    title: "Documentation as a Revenue Strategy",
    audience: "MDS Coordinators and Clinical Leadership",
  },
  {
    title: "The Administrator's Monthly Audit Discipline",
    audience: "New and Aspiring Nursing Home Administrators",
  },
  {
    title: "Translating Operations for Defense Counsel",
    audience: "Healthcare Attorneys, Risk and Insurance",
  },
];

export default function Speaking() {
  useSEO({
    title:
      "Healthcare Speaking and Training · Sierra Thomas · Missouri",
    description:
      "Keynotes, executive workshops, on site training, and CEU programs from Sierra Thomas. Healthcare operations and compliance consultant based in St. Louis, Missouri.",
    path: "/speaking-training",
  });

  return (
    <div data-testid="speaking-page" className="bg-ivory">
      <section className="section-pad pt-32 bg-paper">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-5">Speaking and Training</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
              Bring Sierra into the<br />
              <span className="italic text-teal">conversation.</span>
            </h1>
            <p className="font-sans text-lg text-navy/75 max-w-2xl leading-relaxed">
              Sierra Thomas delivers keynote sessions, executive workshops,
              and department head training programs across long-term care,
              health law, and healthcare risk audiences. Talks are tailored to
              the operator's portfolio. Nothing is delivered off the shelf.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] relative">
              <img
                src={MEDIA.sierraThomas}
                alt="Sierra Thomas speaking"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-40 h-40 border border-gold" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="formats">
        <div className="container-luxe">
          <div className="overline mb-5">Formats</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-tight mb-16 max-w-3xl">
            Calibrated to your audience and outcome.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-champagne-200 border border-champagne-200">
            {[
              { icon: Mic, title: "Keynotes", body: "Thirty to sixty minute strategic addresses for conferences and association events." },
              { icon: GraduationCap, title: "Executive Workshops", body: "Half day to two day intensives for ownership and corporate leadership teams." },
              { icon: Building2, title: "On Site Training", body: "Department head and IDT training delivered inside the facility." },
              { icon: Users, title: "CEU Programs", body: "Continuing education sessions for administrators, social services, and clinical leadership." },
            ].map((f) => (
              <div key={f.title} className="bg-ivory p-8">
                <div className="w-12 h-12 border border-gold flex items-center justify-center mb-5">
                  <f.icon className="h-5 w-5 text-gold" />
                </div>
                <h3 className="font-serif text-xl text-navy mb-3">{f.title}</h3>
                <p className="font-sans text-sm text-navy/70 leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-paper" data-testid="topics">
        <div className="container-luxe">
          <div className="overline mb-5">Signature Topics</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-tight mb-16 max-w-3xl">
            Talks built from inside the building.
          </h2>
          <div className="space-y-4">
            {TOPICS.map((t, i) => (
              <div
                key={t.title}
                className="bg-ivory border border-champagne-200 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-gold transition-colors group"
              >
                <div className="flex gap-6 items-start sm:items-center">
                  <span className="font-serif text-gold text-3xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl text-navy leading-tight group-hover:text-teal transition-colors">
                    {t.title}
                  </h3>
                </div>
                <div className="font-sans text-xs uppercase tracking-overline text-gold flex-shrink-0 sm:text-right">
                  {t.audience}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="speaking-cta-band"
        eyebrow="Book Sierra"
        headline="Bring strategic clarity to your next event."
        sub="Schedule a confidential conversation to discuss agenda, audience, and outcome. Most engagements scope in a single 30 minute call."
      />
    </div>
  );
}
